/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
import { capitalize } from './strings';
import AnalyticsDimensionSingleton from '../components/Header/AnalyticsDimensionSingleton';
import * as AnalyticsRESTClient from './trackers/AnalyticsRESTClient';

// Errors.
export const DEFAULT_ERROR_EVENT_ACTION = 'Error';
export const DEFAULT_ERROR_EVENT_CATEGORY = 'Errores';

export const DEFAULT_ERROR_TYPE = 'Int';
export const DEFAULT_ERROR_CODE = '00001';

/**
 * Send a google analytics event.
 * En PS4, donde `window.ga` no está disponible, el evento se envía
 * directamente usando el Measurement Protocol REST de GA4.
 * En el resto de plataformas se mantiene el flujo original con `window.ga`.
 *
 * @param {object} event Googla Analytics event object.
 * @returns {void}
 */
export function sendGAEvent(event) {
  // Delegar al facade REST cuando está habilitado; solo GA4 recibe el evento
  if (AnalyticsRESTClient.isEnabled()) {
    AnalyticsRESTClient.trackEventGA(event.eventAction, event);
  } else {
    typeof window.ga === 'function' && window.ga('send', event.hitType, event);
  }
}

const analyticsDimensions = new AnalyticsDimensionSingleton();

/**
 * Get an object containing analytics dimensions
 *
 * @return {Object} Object with analytics dimensions
 */
export function getDimensions() {
  return {
    dimension1: analyticsDimensions.getCountry(),
    dimension2: analyticsDimensions.getClientId(),
    dimension3: analyticsDimensions.getUserId(),
    dimension4: analyticsDimensions.getUserType(),

    // Use an empty string since this information is not available at the time of user registration.
    dimension5: '',
    dimension6: analyticsDimensions.getDevice(),
    dimension7: analyticsDimensions.getVersion(),
    dimension8: analyticsDimensions.getDeviceBrand(),
    dimension9: analyticsDimensions.getParentId()
  };
}

/**
 * An object factory for analytics async events.
 * Async events are events that have a start followed by an async action
 * an a success, error or abort event.
 *
 * @param {object} options Metrics options.
 * @param {string} options.label The event label.
 * @param {string} options.action The action that triggered the event.
 * @param {string} options.category The category the event belongs to.
 * @param {string} options.successLabel The label used in case a success event is sent.
 * @param {string} options.errorLabelPrefix The label prefix used in case an error event is sent.
 * This is a prefix because all error events are appended an API error code.
 * @param {Function} [options.send] Function to send the analytics to a server.
 * By default they will be send to google analytics.
 * @param {string} [options.abortLabel] The label using when an event is aborted.
 */
export function createAsyncEvent(options) {
  let started = false;
  let completed = false;
  const { send = sendGAEvent } = options;

  return {
    extra,
    start,
    error,
    abort,
    success,
    hasStarted,
    getDimensions
  };

  /**
   * Notify that the event was successfully completed.
   *
   * @returns {void}
   */
  function success() {
    const dimensions = getDimensions();
    const event = getSuccessEvent(dimensions);

    send(event);
    started = false;
    completed = true;
  }

  /**
   * Notify that an event has started.
   *
   * @returns {void}
   */
  function start() {
    const dimensions = getDimensions();
    const event = getEvent(dimensions, options.label);

    send(event);
    started = true;
    completed = false;
  }

  /**
   * Notify that an error ocurred during the event start and it's completion.
   *
   * @param {Error} e The error that ocurred.
   * @returns {void}
   */
  function error(e) {
    const dimensions = getDimensions();
    const event = getErrorEvent(dimensions, e);

    send(event);
    started = false;
    completed = true;
  }

  /**
   * Notify that an event was aborted before it's completion.
   *
   * @returns {void}
   */
  function abort() {
    const dimensions = getDimensions();
    const event = getAbortEvent(dimensions);

    if (completed) {
      throw new Error(
        `Event "${options.label}" has already been completed and it has not been restarted`
      );
    }

    send(event);
    started = false;
    completed = true;
  }

  /**
   * Send an event that doesn't belong to the start, success, abort, fail flow.
   *
   * @param {object} event Event options.
   * @returns {void}
   */
  function extra(event) {
    const dimensions = getDimensions(dimensions);
    const e = {
      hitType: 'event',
      eventLabel: event.label || options.label,
      eventAction: event.action || options.action,
      eventCategory: event.category || options.category,
      ...dimensions
    };

    send(e);
  }

  /**
   * Check if the event has started.
   *
   * @returns {boolean} True if the event has started.
   */
  function hasStarted() {
    return started;
  }

  /**
   * Get Google Analytic's generic `event` payload
   *
   * @param {Object} dimensions Google Analytic's dimensions to include
   * @param {string} label Event label
   * @return {string} Event payload
   */
  function getEvent(dimensions, label) {
    return {
      hitType: 'event',
      eventLabel: label,
      eventAction: options.action,
      eventCategory: options.category,
      ...dimensions
    };
  }

  /**
   * Get Google Analytic's payload for success events
   *
   * @param {Object} dimensions Google Analytic's dimensions to include
   * @return {string} Event payload
   */
  function getSuccessEvent(dimensions) {
    return {
      hitType: 'event',
      eventLabel: options.successLabel,
      eventAction: options.action,
      eventCategory: options.category,
      ...dimensions
    };
  }

  /**
   * Get Google Analytic's payload for error events
   *
   * @param {Object} dimensions Google Analytic's dimensions to include
   * @param {Object} error Error object
   * @return {Object} Analytics error event
   */
  function getErrorEvent(dimensions, error) {
    const code = (error && error.code) || DEFAULT_ERROR_CODE;
    const type = (error && error.type) ? capitalize(error.type.substr(0, 3)) : DEFAULT_ERROR_TYPE;
    const label = `${options.errorLabelPrefix}-STV_API_${code}-${type}`;

    return {
      hitType: 'event',
      eventLabel: label,
      eventAction: DEFAULT_ERROR_EVENT_ACTION,
      eventCategory: DEFAULT_ERROR_EVENT_CATEGORY,
      ...dimensions
    };
  }

  /**
   * Get Google Analytic's payload for abort events
   *
   * @param {object} dimensions Google Analytic's dimensions.
   * @returns {object} Abort event.
   */
  function getAbortEvent(dimensions) {
    return {
      hitType: 'event',
      eventLabel: options.abortLabel,
      eventAction: options.action,
      eventCategory: options.category,
      ...dimensions
    };
  }
}



// WEBPACK FOOTER //
// ./src/utils/analytics.js